import { useEffect, useMemo, useState } from 'react';
import { Eye, EyeOff, Pencil, UserPlus, UserX, X } from 'lucide-react';
import {
  getUser,
  creatUser,
  updateUser,
  uploadAssinaturaUsuario,
  getAssinaturaUsuario,
  deleteAssinaturaUsuario,
} from '../services/usersService';
import { enviarCodigoValidacao } from '../services/authService';
import { getSessionRole } from '../services/authSession';
import ImageUploadField from '../components/ImageUploadField';
import { getCidadesByUf, UF_OPTIONS } from '../services/cidadeService';
import ExpandableSearchFilter from '../components/ExpandableSearchFilter';
import RecordViewModal from '../components/RecordViewModal';
import SearchAutocompleteSelect from '../components/SearchAutocompleteSelect';
import { showSuccessToast, showErrorToast } from '../utils/notifications';
import { onlyDigits, formatCpf, formatPhone, formatCep, isControlKey } from '../utils/formatters';
import { asArray } from '../utils/normalizers';
import useSubmitLock from '../hooks/useSubmitLock';

/*
FUNCIONALIDADE PERFIL DE USUARIO
const roleLabels = { admin: 'Admin', consultor: 'Consultor' };
*/

/*
FUNCIONALIDADE CADASTRO RESPONSAVEL
const screenLabels = {
  usuarios: 'Usuarios',
  digitacao: 'Cad Resp Digitacao',
  moldagem: 'Cad Resp Moldagem'
};
const responsavelLabels = {
  geral: 'Usuario geral',
  digitacao: 'Resp. digitacao',
  moldagem: 'Resp. moldagem'
};
*/
/*const mockCidades = [
  { id: 1, nome: 'Sao Paulo' },
  { id: 2, nome: 'Campinas' },
  { id: 3, nome: 'Santos' },
];*/

// Tela de Gestão de Usuários: CRUD com máscaras, senha (mostrar/ocultar) e ativar/desativar (admin).
function UsersPage() {
  const role = getSessionRole();
  const canManage = role === 'admin';
  const canCreate = role === 'admin' || role === 'consultor' || !role;

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [selectedUf, setSelectedUf] = useState('MT');
  const [formData, setFormData] = useState({
    nomeCompleto: '', numrCpf: '', endereco: '', codgCep: '', numrTelefone: '',
    enderecoEmail: '', descSenha: '', urlLinkedin: '', urlInstagram: '',
    idCidade: '', role: 'consultor', empresa: 'Kretos Lab', responsavelTipo: 'geral',
  });
  const [cidades, setCidades] = useState([]);
  const [viewUser, setViewUser] = useState(null);
  // Assinatura/carimbo do usuário (rodapé dos relatórios), editável só pelo admin.
  const [assinaturaValue, setAssinaturaValue] = useState(null);
  const [assinaturaOriginal, setAssinaturaOriginal] = useState(null);
  //const [empresas, setEmpresas] = useState([]);

  // Carrega os usuários da API ao montar.
  useEffect(() => {
    let isMounted = true;
    const loadUsers = async () => {
      try {
        // AJUSTE BACKEND AQUI: endpoint de listagem de usuarios.
        const data = await getUser();
        if (isMounted) setUsers(data);
      } catch (error) {
        if (isMounted) setUsers([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    loadUsers();
    /*const loadEmpresas = async () => {
      try {
        // AJUSTE BACKEND AQUI: endpoint de empresas para popular select no formulario.
        const data = await getEmpresa();
        if (isMounted) setEmpresas(data || []);
      } catch (error) {
        if (isMounted) setEmpresas([]);
      }
    };
    loadEmpresas();*/
    return () => { isMounted = false; };
  }, []);

  // Recarrega as cidades do formulário sempre que a UF selecionada muda.
  useEffect(() => {
    let isMounted = true;
    const loadCidades = async () => {
      try {
        // AJUSTE BACKEND AQUI: endpoint de cidades por UF para o formulario de usuario.
        const data = await getCidadesByUf(selectedUf);
        if (isMounted) {
          setCidades(asArray(data));
        }
      } catch (error) {
        if (isMounted) {
          setCidades([]);
        }
      }
    };
    loadCidades();
    return () => { isMounted = false; };
  }, [selectedUf]);

  const filteredUsers = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return users;

    return users.filter((user) => {
      const displayNome = user.nomeCompleto || user.nome || '';
      const displayEmail = user.enderecoEmail || user.email || '';
      return (
        displayNome.toLowerCase().includes(normalized) ||
        displayEmail.toLowerCase().includes(normalized) ||
        (user.empresa || '').toLowerCase().includes(normalized)
      );
    });
  }, [query, users]);

  const stats = useMemo(() => ({
    total: filteredUsers.length,
    ativos: filteredUsers.filter((u) => u.ativo).length,
    empresas: new Set(filteredUsers.map((u) => u.empresa).filter(Boolean)).size
  }), [filteredUsers]);

  // Handler de onChange por campo do formulário.
  const handleFieldChange = (field) => (event) => setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  // Handler de onChange que aplica uma máscara (CPF/telefone/CEP) antes de salvar.
  const handleMaskedChange = (field, formatter) => (event) => setFormData((prev) => ({ ...prev, [field]: formatter(event.target.value) }));

  // Abre o modal em modo de criação (reseta formulário e UF padrão MT).
  const openModal = () => {
    setFormErrors({});
    setEditingUserId(null);
    setSelectedUf('MT');
    setFormData({
      nomeCompleto: '', numrCpf: '', endereco: '', codgCep: '', numrTelefone: '',
      enderecoEmail: '', descSenha: '', urlLinkedin: '', urlInstagram: '', idCidade: '',
      role: 'consultor', empresa: '', responsavelTipo: 'geral',
    });
    setShowPassword(false);
    setAssinaturaValue(null);
    setAssinaturaOriginal(null);
    setIsModalOpen(true);
  };

  // Abre o modal em modo de edição, preenchendo o formulário com máscaras aplicadas.
  const openEditModal = (user) => {
    setFormErrors({});
    setEditingUserId(user.idUsuario);
    setSelectedUf((user.uf || user.UF || 'MT').toUpperCase());
    setFormData({
      nomeCompleto: user.nomeCompleto || user.nome || '',
      numrCpf: formatCpf(user.numrCpf || ''),
      endereco: user.endereco || '',
      codgCep: formatCep(String(user.codgCep || '')),
      numrTelefone: formatPhone(user.numrTelefone || ''),
      enderecoEmail: user.enderecoEmail || user.email || '',
      descSenha: user.descSenha || '',
      urlLinkedin: user.urlLinkedin || '',
      urlInstagram: user.urlInstagram || '',
      idCidade: user.idCidade || '',
      role: user.role,
      empresa: user.empresa || '',
      responsavelTipo: user.responsavelTipo || 'geral',
    });
    setShowPassword(false);
    setAssinaturaValue(null);
    setAssinaturaOriginal(null);
    setIsModalOpen(true);

    if (canManage) {
      getAssinaturaUsuario(user.idUsuario)
        .then((res) => {
          if (res && res.dataUri) {
            setAssinaturaValue(res.dataUri);
            setAssinaturaOriginal(res.dataUri);
          }
        })
        .catch(() => { });
    }
  };

  // Fecha o modal e limpa edição/erros.
  const closeModal = () => {
    setIsModalOpen(false);
    setEditingUserId(null);
    setFormErrors({});
    setAssinaturaValue(null);
    setAssinaturaOriginal(null);
  };

  // Persiste a assinatura do usuário após salvar. Retorna se ele ficou com assinatura (ou null se não mudou).
  const persistAssinaturaUsuario = async (idUsuario) => {
    if (!canManage) return null;
    try {
      if (assinaturaValue instanceof File) {
        await uploadAssinaturaUsuario(idUsuario, assinaturaValue);
        return true;
      }
      if (assinaturaValue == null && assinaturaOriginal) {
        await deleteAssinaturaUsuario(idUsuario);
        return false;
      }
    } catch (error) {
      showErrorToast(error?.message || 'Usuário salvo, mas não foi possível salvar a assinatura.');
    }
    return null;
  };


  // Valida obrigatórios (nome/CPF/telefone/email/senha) e formato; retorna erros + CEP sem máscara.
  const validateForm = () => {
    const errors = {};
    if (!formData.nomeCompleto.trim()) errors.nomeCompleto = 'Nome completo obrigatório.';
    if (!formData.numrCpf.trim()) errors.numrCpf = 'CPF obrigatório.';
    if (!formData.numrTelefone.trim()) errors.numrTelefone = 'Telefone obrigatório.';
    if (!formData.enderecoEmail.trim()) errors.enderecoEmail = 'Email obrigatório.';
    if (!formData.descSenha.trim()) errors.descSenha = 'Senha obrigatória.';
    //if (!formData.empresa.trim()) errors.empresa = 'Empresa obrigatoria.';
    if (formData.enderecoEmail.trim() && !/\S+@\S+\.\S+/.test(formData.enderecoEmail.trim())) errors.enderecoEmail = 'Email inválido.';

    const cpfDigits = onlyDigits(formData.numrCpf);
    if (formData.numrCpf.trim() && cpfDigits.length !== 11) errors.numrCpf = 'CPF inválido.';

    const cepDigits = onlyDigits(formData.codgCep);
    if (cepDigits && cepDigits.length !== 8) errors.codgCep = 'CEP inválido.';

    return { errors, cepDigits };
  };

  // Submete o form: valida, checa email duplicado, cria/atualiza na API e refaz a lista.
  const [submitting, handleSaveUser] = useSubmitLock(async (event) => {
    event.preventDefault();
    const { errors, cepDigits } = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const emailExists = users.some((u) => {
      if (editingUserId && u.idUsuario === editingUserId) return false;
      return (u.enderecoEmail || u.email || '').toLowerCase() === formData.enderecoEmail.trim().toLowerCase();
    });

    if (emailExists) {
      setFormErrors({ enderecoEmail: 'Email já cadastrado.' });
      return;
    }

    try {
      if (editingUserId) { // service chamado para limpar e formatar os dados brutos
        // AJUSTE BACKEND AQUI: fluxo de update de usuario.
        const { payload } = await updateUser(editingUserId, formData, cepDigits);
        const possuiAssinatura = await persistAssinaturaUsuario(editingUserId);
        setUsers((prev) => prev.map((u) => {
          if (u.idUsuario !== editingUserId) return u;
          return possuiAssinatura == null ? { ...u, ...payload } : { ...u, ...payload, possuiAssinatura };
        }));
        showSuccessToast('Usuário atualizado com sucesso.');
      } else {
        // AJUSTE BACKEND AQUI: fluxo de criacao de usuario.
        const { response: novoUsuario, payload } = await creatUser(formData, cepDigits);
        // Sem id real da API não dá para gravar a assinatura agora.
        const possuiAssinatura = novoUsuario?.idUsuario
          ? await persistAssinaturaUsuario(novoUsuario.idUsuario)
          : null;
        const normalizedUser = {
          ...payload,
          idUsuario: novoUsuario?.idUsuario || `usr-${Date.now()}`,
          ativo: novoUsuario?.ativo ?? true,
          possuiAssinatura: Boolean(possuiAssinatura),
        };
        setUsers((prev) => [normalizedUser, ...prev]);
        try {
          await enviarCodigoValidacao(formData.enderecoEmail.trim());
          showSuccessToast('Usuário cadastrado com sucesso. Código de verificação enviado por e-mail.');
        } catch (sendError) {
          showSuccessToast('Usuário cadastrado com sucesso.');
          showErrorToast('Não foi possível enviar o e-mail de verificação agora. Tente reenviar mais tarde.');
        }
      }
      closeModal();
    } catch (error) {
      setFormErrors({ geral: error?.message || 'Não foi possível salvar o usuário.' });
    }
  });

  // Alterna ativo/inativo do usuário na lista local (restrito a admin).
  const toggleUserStatus = (userId) => {
    if (!canManage) return;
    setUsers((prev) => prev.map((u) => u.idUsuario === userId ? { ...u, ativo: !u.ativo } : u));
  };
  return (
    <div className="users-page space-y-6">
      <section className="users-header flex flex-col gap-4 rounded-2xl p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900 sm:text-3xl">Gestão de usuários</h2>
          <p className="mt-2 max-w-2xl text-sm text-slate-500">
            Visualize e gerencie os usuários cadastrados no sistema
          </p>
        </div>
        <button
          type="button"
          onClick={openModal}
          className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm transition hover:border-blue-200 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
          disabled={!canCreate}
        >
          <UserPlus className="h-4 w-4" />
          Novo usuário
        </button>
      </section>

      {/*
      FUNCIONALIDADE CADASTRO RESPONSAVEL
      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Telas de cadastro</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setActiveScreen('usuarios')}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${activeScreen === 'usuarios'
              ? 'border-[#0e7490] bg-[#0e7490]/10 text-[#0e7490]'
              : 'border-slate-200 text-slate-600 hover:border-blue-200 hover:text-blue-600'
              }`}
          >
            Usuários
          </button>
          <button
            type="button"
            onClick={() => setActiveScreen('digitacao')}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${activeScreen === 'digitacao'
              ? 'border-[#0e7490] bg-[#0e7490]/10 text-[#0e7490]'
              : 'border-slate-200 text-slate-600 hover:border-blue-200 hover:text-blue-600'
              }`}
          >
            Cad Resp Digitação
          </button>
          <button
            type="button"
            onClick={() => setActiveScreen('moldagem')}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${activeScreen === 'moldagem'
              ? 'border-[#0e7490] bg-[#0e7490]/10 text-[#0e7490]'
              : 'border-slate-200 text-slate-600 hover:border-blue-200 hover:text-blue-600'
              }`}
          >
            Cad Resp Moldagem
          </button>
        </div>
      </section>
      */}

      <section className="users-metrics grid gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Total</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">{stats.total}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Ativos</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">{stats.ativos}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Empresas</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">{stats.empresas}</p>
        </div>
      </section>

      <section className="users-table overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-4 py-3 sm:hidden">
          <ExpandableSearchFilter
            query={query}
            onChange={setQuery}
            placeholder="Buscar por nome, email ou empresa"
            resultCount={filteredUsers.length}
          />
        </div>

        <div className="hidden overflow-x-auto sm:block">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-[0.3em] text-slate-400">
              <tr>
                <th className="px-4 py-3">Nome</th>
                <th className="px-4 py-3">Email</th>
                {/*<th className="px-4 py-3">Empresa</th>*/}
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <span>Ações</span>
                    <ExpandableSearchFilter
                      variant="inline"
                      query={query}
                      onChange={setQuery}
                      placeholder="Buscar..."
                      resultCount={filteredUsers.length}
                    />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <tr key={`skeleton-${index}`} className="border-b border-slate-100">
                    {Array.from({ length: 4 }).map((__, cellIndex) => (
                      <td key={cellIndex} className="px-4 py-4">
                        <div className="h-4 w-full animate-pulse rounded bg-slate-100" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-4 py-6 text-center text-sm text-slate-500"
                  >
                    Nenhum usuário encontrado.
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.idUsuario} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/80">
                    <td className="px-4 py-4">
                      <div className="font-medium text-slate-900">
                        {user.nomeCompleto || user.nome}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-slate-600">
                      {user.enderecoEmail || user.email}
                    </td>
                    {/*
                    FUNCIONALIDADE PERFIL DE USUARIO
                    <td className="px-4 py-4 text-slate-600">
                      <span className="rounded-full bg-[#0e7490]/10 px-2.5 py-1 text-xs font-medium text-[#0e7490]">
                        {roleLabels[user.role] || user.role}
                      </span>
                    </td>
                    */}
                    {/*
                    FUNCIONALIDADE CADASTRO RESPONSAVEL
                    <td className="px-4 py-4 text-slate-600">
                      <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
                        {responsavelLabels[user.responsavelTipo || 'geral']}
                      </span>
                    </td>
                    */}
                    {/*<td className="px-4 py-4 text-slate-600">{user.empresa}</td>*/}
                    <td className="px-4 py-4">
                      <span
                        className={`rounded-full px-2.5 py-1 text-xs font-medium ${user.ativo
                          ? 'bg-[#0e7490]/10 text-[#0e7490]'
                          : 'bg-slate-100 text-slate-600'
                          }`}
                      >
                        {user.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setViewUser(user)}
                          className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-[#0e7490]/40 hover:text-[#0e7490]"
                        >
                          <Eye className="h-3.5 w-3.5" />
                          Visualizar
                        </button>
                        {canManage ? (
                          <>
                            <button
                              type="button"
                              onClick={() => openEditModal(user)}
                              className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-600"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                              Editar
                            </button>
                            <button
                              type="button"
                              onClick={() => toggleUserStatus(user.idUsuario)}
                              className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-600"
                            >
                              <UserX className="h-3.5 w-3.5" />
                              {user.ativo ? 'Desativar' : 'Ativar'}
                            </button>
                          </>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="space-y-3 p-4 sm:hidden">
          {loading ? (
            Array.from({ length: 4 }).map((_, index) => (
              <div key={`mobile-skeleton-${index}`} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="h-4 w-1/2 animate-pulse rounded bg-slate-100" />
                <div className="mt-3 h-3 w-2/3 animate-pulse rounded bg-slate-100" />
                <div className="mt-3 h-3 w-1/3 animate-pulse rounded bg-slate-100" />
              </div>
            ))
          ) : filteredUsers.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
              Nenhum usuário encontrado.
            </div>
          ) : (
            filteredUsers.map((user) => (
              <div key={user.idUsuario} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-base font-semibold text-slate-900">
                      {user.nomeCompleto || user.nome}
                    </p>
                    <p className="text-xs text-slate-500">{user.enderecoEmail || user.email}</p>
                  </div>
                  <span
                    className={`rounded-full px-2 py-1 text-xs font-medium ${user.ativo
                      ? 'bg-[#0e7490]/10 text-[#0e7490]'
                      : 'bg-slate-100 text-slate-600'
                      }`}
                  >
                    {user.ativo ? 'Ativo' : 'Inativo'}
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {/*
                  FUNCIONALIDADE PERFIL DE USUARIO
                  <span className="rounded-full bg-[#0e7490]/10 px-2.5 py-1 text-xs font-medium text-[#0e7490]">
                    {roleLabels[user.role] || user.role}
                  </span>
                  */}
                  {/*
                  FUNCIONALIDADE CADASTRO RESPONSAVEL
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
                    {responsavelLabels[user.responsavelTipo || 'geral']}
                  </span>
                  */}
                  <span className="text-xs text-slate-500">{user.empresa}</span>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setViewUser(user)}
                    className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-[#0e7490]/40 hover:text-[#0e7490]"
                  >
                    <Eye className="h-3.5 w-3.5" />
                    Visualizar
                  </button>
                  {canManage ? (
                    <>
                      <button
                        type="button"
                        onClick={() => openEditModal(user)}
                        className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-600"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                        Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleUserStatus(user.idUsuario)}
                        className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-600"
                      >
                        <UserX className="h-3.5 w-3.5" />
                        {user.ativo ? 'Desativar' : 'Ativar'}
                      </button>
                    </>
                  ) : null}
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      <RecordViewModal
        open={!!viewUser}
        onClose={() => setViewUser(null)}
        title="Detalhes do usuário"
        sections={viewUser ? [
          {
            title: 'Dados gerais',
            fields: [
              { label: 'Nome completo', value: viewUser.nomeCompleto || viewUser.nome || '-' },
              { label: 'Email', value: viewUser.enderecoEmail || viewUser.email || '-' },
              { label: 'CPF', value: formatCpf(viewUser.numrCpf || '') || '-' },
              { label: 'Telefone', value: formatPhone(viewUser.numrTelefone || '') || '-' },
              { label: 'Status', value: viewUser.ativo ? 'Ativo' : 'Inativo' },
            ],
          },
          {
            title: 'Endereço',
            fields: [
              { label: 'Endereço', value: viewUser.endereco || '-' },
              { label: 'CEP', value: formatCep(String(viewUser.codgCep || '')) || '-' },
              { label: 'Cidade', value: viewUser.nomeCidade || viewUser.idCidade || '-' },
            ],
          },
          {
            title: 'Redes sociais',
            fields: [
              { label: 'LinkedIn', value: viewUser.urlLinkedin || '-' },
              { label: 'Instagram', value: viewUser.urlInstagram || '-' },
            ],
          },
        ] : []}
      />

      {isModalOpen ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center px-4 py-4 sm:py-0">
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/40"
            onClick={closeModal}
            aria-label="Fechar modal"
          />
          <div className="relative z-10 w-full max-w-sm sm:max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-4 shadow-xl sm:p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Novo</p>
                <h3 className="text-xl font-semibold text-slate-900">
                  {editingUserId ? 'Editar usuario' : 'Cadastrar usuario'}
                </h3>
                {/*
                FUNCIONALIDADE CADASTRO RESPONSAVEL
                Titulo dinamico por categoria de responsavel.
                */}
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                aria-label="Fechar"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <form onSubmit={handleSaveUser} className="mt-6 space-y-3 sm:space-y-4">
              {formErrors.geral ? (
                <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                  {formErrors.geral}
                </div>
              ) : null}
              <div className="space-y-2">
                <label htmlFor="user-nome" className="text-sm font-medium text-slate-700">
                  Nome completo <span className="text-rose-500">*</span>
                </label>
                <input
                  id="user-nome"
                  type="text"
                  value={formData.nomeCompleto}
                  onChange={handleFieldChange('nomeCompleto')}
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />
                {formErrors.nomeCompleto ? (
                  <p className="text-xs text-rose-600">{formErrors.nomeCompleto}</p>
                ) : null}
              </div>
              <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2">
                <div className="space-y-2">
                  <label htmlFor="user-cpf" className="text-sm font-medium text-slate-700">
                    CPF <span className="text-rose-500">*</span>
                  </label>
                  <input
                    id="user-cpf"
                    type="text"
                    value={formData.numrCpf}
                    onChange={handleMaskedChange('numrCpf', formatCpf)}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                  {formErrors.numrCpf ? (
                    <p className="text-xs text-rose-600">{formErrors.numrCpf}</p>
                  ) : null}
                </div>
                <div className="space-y-2">
                  <label htmlFor="user-telefone" className="text-sm font-medium text-slate-700">
                    Telefone <span className="text-rose-500">*</span>
                  </label>
                  <input
                    id="user-telefone"
                    type="text"
                    value={formData.numrTelefone}
                    onChange={handleMaskedChange('numrTelefone', formatPhone)}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                  {formErrors.numrTelefone ? (
                    <p className="text-xs text-rose-600">{formErrors.numrTelefone}</p>
                  ) : null}
                </div>
              </div>
              <div className="space-y-2">
                <label htmlFor="user-endereco" className="text-sm font-medium text-slate-700">
                  Endereço:
                </label>
                <input
                  id="user-endereco"
                  type="text"
                  value={formData.endereco}
                  onChange={handleFieldChange('endereco')}
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />
              </div>
              <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-3">
                <div className="space-y-2">
                  <label htmlFor="user-cep" className="text-sm font-medium text-slate-700">
                    CEP:
                  </label>
                  <input
                    id="user-cep"
                    type="text"
                    inputMode="numeric"
                    value={formData.codgCep}
                    onChange={handleMaskedChange('codgCep', formatCep)}
                    onKeyDown={(event) => {
                      if (isControlKey(event)) {
                        return;
                      }
                      if (event.key.length === 1 && !/\d/.test(event.key)) {
                        event.preventDefault();
                      }
                    }}
                    onPaste={(event) => {
                      const pasted = event.clipboardData.getData('text');
                      if (/\D/.test(pasted)) {
                        event.preventDefault();
                      }
                    }}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                  {formErrors.codgCep ? (
                    <p className="text-xs text-rose-600">{formErrors.codgCep}</p>
                  ) : null}
                </div>
                <div className="space-y-2">
                  <label htmlFor="user-uf" className="text-sm font-medium text-slate-700">
                    UF:
                  </label>
                  <select
                    id="user-uf"
                    value={selectedUf}
                    onChange={(event) => {
                      const uf = event.target.value;
                      setSelectedUf(uf);
                      setFormData((prev) => ({ ...prev, idCidade: '' }));
                    }}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  >
                    {UF_OPTIONS.map((uf) => (
                      <option key={uf} value={uf}>
                        {uf}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <label htmlFor="user-cidade" className="text-sm font-medium text-slate-700">
                    Cidade:
                  </label>
                  <SearchAutocompleteSelect
                    id="user-cidade"
                    value={formData.idCidade}
                    onChange={handleFieldChange('idCidade')}
                    placeholder="Buscar cidade"
                    options={cidades.map((cidade) => ({
                      id: cidade.idCidade || cidade.id,
                      label: cidade.nomeCidade || cidade.nome,
                    }))}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label htmlFor="user-email" className="text-sm font-medium text-slate-700">
                  Email <span className="text-rose-500">*</span>
                </label>
                <input
                  id="user-email"
                  type="email"
                  value={formData.enderecoEmail}
                  onChange={handleFieldChange('enderecoEmail')}
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />
                {formErrors.enderecoEmail ? (
                  <p className="text-xs text-rose-600">{formErrors.enderecoEmail}</p>
                ) : null}
              </div>
              <div className="space-y-2">
                <label htmlFor="user-senha" className="text-sm font-medium text-slate-700">
                  Senha <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <input
                    id="user-senha"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.descSenha}
                    onChange={handleFieldChange('descSenha')}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 pr-10 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-400 transition hover:text-slate-600"
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {formErrors.descSenha ? (
                  <p className="text-xs text-rose-600">{formErrors.descSenha}</p>
                ) : null}
              </div>
              <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2">
                <div className="space-y-2">
                  <label htmlFor="user-linkedin" className="text-sm font-medium text-slate-700">
                    Linkedin:
                  </label>
                  <input
                    id="user-linkedin"
                    type="text"
                    value={formData.urlLinkedin}
                    onChange={handleFieldChange('urlLinkedin')}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="user-instagram" className="text-sm font-medium text-slate-700">
                    Instagram:
                  </label>
                  <input
                    id="user-instagram"
                    type="text"
                    value={formData.urlInstagram}
                    onChange={handleFieldChange('urlInstagram')}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                </div>
              </div>
              {/*
              FUNCIONALIDADE CADASTRO RESPONSAVEL E PERFIL DE USUARIO
              <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2">
                <div className="space-y-2">
                  <label htmlFor="user-categoria" className="text-sm font-medium text-slate-700">
                    Categoria de cadastro
                  </label>
                  <select
                    id="user-categoria"
                    value={formData.responsavelTipo}
                    onChange={handleFieldChange('responsavelTipo')}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    disabled={Boolean(editingUserId)}
                  >
                    <option value="geral">Usuário geral</option>
                    <option value="digitacao">Resp. digitação</option>
                    <option value="moldagem">Resp. moldagem</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label htmlFor="user-role" className="text-sm font-medium text-slate-700">
                    Perfil
                  </label>
                  <select
                    id="user-role"
                    value={formData.role}
                    onChange={handleFieldChange('role')}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  >
                    <option value="consultor">Consultor</option>
                    {canManage ? <option value="admin">Admin</option> : null}
                  </select>
                </div>
              </div>
              */}
              {/*<div className="grid gap-3 sm:gap-4 grid-cols-1">
                <div className="space-y-2">
                  <label htmlFor="user-empresa" className="text-sm font-medium text-slate-700">
                    Empresa
                  </label>
                  <select
                    id="user-empresa"
                    value={formData.empresa}
                    onChange={handleFieldChange('empresa')}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  >
                    <option value="">Selecione</option>
                    {empresas.map((empresa) => {
                      const nomeEmpresa = empresa.razao_social || empresa.nome_fantasia || empresa.nome || '';
                      const optionKey = empresa.id_empresa || nomeEmpresa;
                      if (!nomeEmpresa) {
                        return null;
                      }
                      return (
                        <option key={optionKey} value={nomeEmpresa}>
                          {nomeEmpresa}
                        </option>
                      );
                    })}
                  </select>
                  {formErrors.empresa ? (
                    <p className="text-xs text-rose-600">{formErrors.empresa}</p>
                  ) : null}
                </div>
              </div>*/}
              {canManage ? (
                <div className="rounded-lg border border-slate-200 p-3">
                  <ImageUploadField
                    id="user-assinatura"
                    label="Assinatura / carimbo (rodapé dos relatórios)"
                    hint="Opcional. Só usuários com assinatura podem ser vinculados como assinantes no cadastro de empresa. PNG, JPEG ou WEBP até 2 MB."
                    value={assinaturaValue}
                    onChange={setAssinaturaValue}
                    disabled={submitting}
                  />
                </div>
              ) : null}
              <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 sm:gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="rounded-md border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-600 w-full sm:w-auto"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60 w-full sm:w-auto"
                >
                  {submitting ? 'Salvando...' : editingUserId ? 'Salvar alterações' : 'Salvar usuário'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default UsersPage;

